import { ChangeDetectionStrategy, Component, computed, Input, type OnDestroy, type OnInit, signal } from '@angular/core'
import { FormBuilder, type FormControl, type FormGroup, ReactiveFormsModule } from '@angular/forms'
import { CommonModule } from '@angular/common'
import { CptGeolocationService, CptGoogleMapsService, CustomerPortalSharedModule, TransifexService } from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { NGXLogger } from 'ngx-logger'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { type Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { type User } from '@inside-hub-app/customer-portal-b2c-client'
import { SharedService } from '../../../services/shared.service'
import { DataService } from '../../../services/data.service'
import { FuelPricesService } from '../../../services/fuel-prices.service'
import { type FuelPriceDTO, VehiclesService, type VehicleDTOExtended } from '../../../services/vehicles.service'
import { type GasPricesPlacePrediction, type ResolvedLocation } from '../../../models/fuel-prices.models'

declare const google: typeof globalThis.google

const FUEL_TYPES = ['LPG', 'diesel', 'petrol', 'CNG', 'LNG']
const DEFAULT_FUEL_TYPE = 'diesel'
const RADIUS_OPTIONS = [5, 10, 15, 20, 25]
const DEFAULT_MAX_ITEMS = 10
const DEFAULT_SORT = 'price'
const GOOGLE_MAPS_SEARCH_URL = 'https://www.google.com/maps/search/?api=1&query='

@Component({
  selector: 'customer-portal-app-revolution-vehicle-gas-prices',
  standalone: true,
  templateUrl: './vehicle-gas-prices.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule
  ]
})
export class VehicleGasPricesComponent implements OnInit, OnDestroy {
  @Input()
    vehicle: VehicleDTOExtended | undefined

  fuelTypes = FUEL_TYPES
  radiusOptions = RADIUS_OPTIONS

  filterForm: FormGroup<{ fuelType: FormControl<string>, radius: FormControl<number> }>
  locationControl: FormControl<string | GasPricesPlacePrediction>
  sortControl: FormControl<string>

  predictions = signal<GasPricesPlacePrediction[]>([])
  selectedPlace = signal<ResolvedLocation | null>(null)
  locationMissing = signal(false)
  locationError = signal(false)
  locatingMe = signal(false)
  // Whether the results currently on screen were fetched from the user's own
  // position. Drives the distance column, distance sorting and map routing.
  usedCurrentLocation = signal(false)

  fuelPrices = signal<FuelPriceDTO[] | null>(null)
  loading = signal(false)
  error = signal(false)
  searched = signal(false)

  canSearch = computed(() => this.selectedPlace() != null)
  canSortByDistance = computed(() => this.usedCurrentLocation())

  user: User | null = null

  public country: string

  private autocompleteService: google.maps.places.AutocompleteService | undefined
  private placesService: google.maps.places.PlacesService | undefined
  private placesServiceNode: HTMLDivElement | undefined
  private mapsLoaded = false
  // Whether the location held by the form came from the browser geolocation.
  // Tracked separately from usedCurrentLocation so that re-submitting the
  // search (e.g. after changing the radius) keeps the current-location
  // context instead of silently downgrading to a plain coordinate search.
  private locationFromCurrentPosition = false

  sub: Record<string, Subscription | null> = {
    onUserLoaded: null,
    onMapsLoaded: null,
    locationValueChanges: null,
    filterFormValueChanges: null,
    sortValueChanges: null,
    getFuelPrices: null,
    geolocation: null
  }

  constructor (
    private readonly formBuilder: FormBuilder,
    private readonly vehiclesService: VehiclesService,
    private readonly dataService: DataService,
    public sharedService: SharedService,
    private readonly logger: NGXLogger,
    private readonly transifexService: TransifexService,
    private readonly googleMapsService: CptGoogleMapsService,
    private readonly geolocationService: CptGeolocationService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>,
    public fuelPricesService: FuelPricesService
  ) {
    this.country = this.remoteConfigService.get('country.code')
    this.filterForm = this.formBuilder.nonNullable.group({
      fuelType: DEFAULT_FUEL_TYPE,
      radius: RADIUS_OPTIONS[0]
    })
    this.locationControl = this.formBuilder.nonNullable.control<string | GasPricesPlacePrediction>('')
    this.sortControl = this.formBuilder.nonNullable.control<string>(DEFAULT_SORT)
  }

  ngOnInit (): void {
    const defaultFuelType = this.vehicle?.details?.engine?.fuel?.name
    if (defaultFuelType != null && this.fuelTypes.includes(defaultFuelType)) {
      this.filterForm.patchValue({ fuelType: defaultFuelType })
    }

    this.sub.onUserLoaded = this.dataService.onUserLoaded.subscribe(user => {
      this.user = user
    })

    this.sub.onMapsLoaded = this.googleMapsService.onMapsLoaded.subscribe(loaded => {
      this.mapsLoaded = loaded
    })

    this.sub.locationValueChanges = this.locationControl.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(value => {
        if (typeof value === 'string' && value.trim().length === 0) {
          this.clearLoadedFuelPrices()
        }
        this.searchPredictions(value)
      })

    this.sub.filterFormValueChanges = this.filterForm.valueChanges
      .subscribe(() => {
        this.locationError.set(false)
      })

    this.sub.sortValueChanges = this.sortControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        if (this.searched() && this.selectedPlace() != null) {
          this.onSubmit()
        }
      })
  }

  ngOnDestroy (): void {
    this.sharedService.unsubscribe(this.sub)
  }

  searchPredictions (value: string | GasPricesPlacePrediction): void {
    if (!this.mapsLoaded || typeof value !== 'string' || value.length < 3) {
      this.predictions.set([])
      return
    }

    this.autocompleteService ??= new google.maps.places.AutocompleteService()

    void this.autocompleteService.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: this.country?.toLowerCase() },
        types: ['(regions)']
      },
      (predictions, status) => {
        this.predictions.set(
          status === google.maps.places.PlacesServiceStatus.OK && predictions != null
            ? predictions.map(prediction => ({
              placeId: prediction.place_id,
              description: prediction.description
            }))
            : []
        )
      }
    )
  }

  selectPrediction (prediction: GasPricesPlacePrediction): void {
    if (prediction == null) {
      return
    }

    // Picking a place from the autocomplete replaces any geolocation result.
    this.locationFromCurrentPosition = false
    this.locationError.set(false)

    this.placesServiceNode ??= document.createElement('div')
    this.placesService ??= new google.maps.places.PlacesService(this.placesServiceNode)

    this.placesService.getDetails(
      {
        placeId: prediction.placeId,
        fields: ['address_components', 'geometry']
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place != null) {
          this.selectedPlace.set(this.fuelPricesService.mapAddressComponents(place))
          this.locationMissing.set(false)
        } else {
          this.selectedPlace.set(null)
          this.logger.debug('Google place details error')
        }
        this.predictions.set([])
        this.logger.debug('Selected place', this.selectedPlace())
      }
    )
  }

  clearLocation (): void {
    this.locationControl.setValue('', { emitEvent: false })
    this.predictions.set([])
    this.selectedPlace.set(null)
    this.locationMissing.set(false)
    this.usedCurrentLocation.set(false)
    this.locationFromCurrentPosition = false
    this.sortControl.setValue(DEFAULT_SORT, { emitEvent: false })
    this.clearLoadedFuelPrices()
  }

  clearLoadedFuelPrices (): void {
    this.fuelPrices.set(null)
    this.searched.set(false)
    this.loading.set(false)
    this.error.set(false)
  }

  useMyLocation (): void {
    if (this.locatingMe()) {
      return
    }
    this.locatingMe.set(true)
    this.locationError.set(false)

    this.sub.geolocation = this.geolocationService.getCurrentPosition()
      .subscribe(position => {
        const coords = position?.coords
        const lat = coords?.latitude
        const lon = coords?.longitude
        if (lat != null && lon != null) {
          this.resolveCurrentLocation(lat, lon)
        } else {
          this.locatingMe.set(false)
          this.locationError.set(true)
        }
      })
  }

  resolveCurrentLocation (lat: number, lon: number): void {
    const currentLocationLabel = this.transifexService.getTransifexTranslation(
      'Current location',
      'customerPortal.customer-portal.gas-prices.current-location'
    )

    this.selectedPlace.set({ lat, lon })
    this.locationControl.setValue({ placeId: '', description: currentLocationLabel }, { emitEvent: false })
    this.predictions.set([])
    this.locationMissing.set(false)
    this.locatingMe.set(false)
    this.locationFromCurrentPosition = true

    this.onSubmit()
  }

  onSubmit (): void {
    this.locationError.set(false)
    this.usedCurrentLocation.set(this.locationFromCurrentPosition)

    const place = this.selectedPlace()
    const zip = place?.zip ?? this.user?.address?.[0]?.zip
    let coords = null
    if (place?.lat != null) {
      coords = { latitude: place.lat, longitude: place.lon }
    }

    if (zip == null && coords == null) {
      this.locationMissing.set(true)
      return
    }

    this.locationMissing.set(false)
    this.searched.set(true)
    this.loading.set(true)
    this.error.set(false)
    this.fuelPrices.set(null)

    this.sharedService.unsubscribe({ getFuelPrices: this.sub.getFuelPrices })

    const { fuelType, radius } = this.filterForm.getRawValue()

    let sort = this.sortControl.getRawValue()
    if (sort === 'distance' && !this.usedCurrentLocation()) {
      sort = DEFAULT_SORT
      this.sortControl.setValue(sort, { emitEvent: false })
    }

    this.sub.getFuelPrices = this.vehiclesService
      .getFuelPrices(fuelType, zip, coords, radius, DEFAULT_MAX_ITEMS, sort)
      .subscribe({
        next: prices => {
          this.fuelPrices.set(prices)
          this.loading.set(false)
        },
        error: error => {
          this.logger.debug(error)
          this.error.set(true)
          this.loading.set(false)
        }
      })
  }

  openMap (fuelStation: FuelPriceDTO): void {
    const latLonD = String(fuelStation.lat) + ',' + String(fuelStation.lon)
    const place = this.selectedPlace()

    // Routing only makes sense when the search was based on the user's
    // current location; for a free-text city search the origin is unknown,
    // so just drop a pin on the station instead.
    if (this.usedCurrentLocation() && place?.lat != null) {
      const latLonO = String(place.lat) + ',' + String(place.lon)
      window.open(
        this.remoteConfigService.get('googleMaps.googleMapsUrl') +
          latLonO +
          '&destination=' +
          latLonD +
          '&travelmode=driving',
        '_blank'
      )
      return
    }

    window.open(
      GOOGLE_MAPS_SEARCH_URL + latLonD,
      '_blank'
    )
  }
}
